"use client";

import { Capacitor } from "@capacitor/core";
import { getAppleMusicConnectToken } from "@/actions/music";
import type { MusicSong } from "@/lib/music/types";

/**
 * Optional: connect your own Apple Music account (MusicKit JS) to pick from
 * what you have actually been playing. Catalog search never needs this.
 *
 * Web only. MusicKit's sign-in is a popup to apple.com, which the native
 * shells' WebViews don't host reliably; native members still get the full
 * catalog search, which is the whole feature minus a shortcut.
 *
 * The member's Music User Token lives in MusicKit's own storage on their
 * device and is sent only to api.music.apple.com — never to our server. We
 * read song ids out of the response and save through the same server action
 * as search, which re-reads every field from the catalog.
 */

const MUSICKIT_SRC = "https://js-cdn.music.apple.com/musickit/v3/musickit.js";
const CONNECTED_FLAG = "hiive.music.appleConnected";

interface MusicKitInstance {
  isAuthorized: boolean;
  authorize: () => Promise<string>;
  unauthorize: () => Promise<void>;
  api: {
    music: (
      path: string,
      params?: Record<string, string | number>,
    ) => Promise<{ data?: { data?: unknown[] } }>;
  };
}

interface MusicKitGlobal {
  configure: (options: {
    developerToken: string;
    app: { name: string; build: string };
    storefrontId?: string;
  }) => Promise<MusicKitInstance> | MusicKitInstance;
  getInstance: () => MusicKitInstance;
}

declare global {
  interface Window {
    MusicKit?: MusicKitGlobal;
  }
}

export function isAppleMusicConnectSupported(): boolean {
  if (typeof window === "undefined") return false;
  return !Capacitor.isNativePlatform();
}

export function wasAppleMusicConnected(): boolean {
  try {
    return window.localStorage.getItem(CONNECTED_FLAG) === "1";
  } catch {
    return false;
  }
}

function rememberConnected(connected: boolean) {
  try {
    if (connected) window.localStorage.setItem(CONNECTED_FLAG, "1");
    else window.localStorage.removeItem(CONNECTED_FLAG);
  } catch {
    // Private mode: the member just reconnects next time.
  }
}

let loading: Promise<MusicKitInstance> | null = null;

function loadScript(): Promise<MusicKitGlobal> {
  if (window.MusicKit) return Promise.resolve(window.MusicKit);
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = MUSICKIT_SRC;
    script.async = true;
    script.crossOrigin = "anonymous";
    const timeout = window.setTimeout(
      () => reject(new Error("MusicKit load timed out")),
      15_000,
    );
    document.addEventListener(
      "musickitloaded",
      () => {
        window.clearTimeout(timeout);
        if (window.MusicKit) resolve(window.MusicKit);
        else reject(new Error("MusicKit missing after load"));
      },
      { once: true },
    );
    script.onerror = () => {
      window.clearTimeout(timeout);
      reject(new Error("MusicKit failed to load"));
    };
    document.head.appendChild(script);
  });
}

async function getMusicKit(): Promise<MusicKitInstance> {
  loading ??= (async () => {
    const token = await getAppleMusicConnectToken();
    if (!token.ok) throw new Error(token.message);
    const MusicKit = await loadScript();
    await MusicKit.configure({
      developerToken: token.data.developerToken,
      app: { name: "Hiive", build: "1" },
      storefrontId: token.data.storefront,
    });
    return MusicKit.getInstance();
  })().catch((error) => {
    loading = null;
    throw error;
  });
  return loading;
}

/** Opens Apple's sign-in. Resolves true once the member is connected. */
export async function connectAppleMusic(): Promise<boolean> {
  const music = await getMusicKit();
  if (!music.isAuthorized) await music.authorize();
  rememberConnected(music.isAuthorized);
  return music.isAuthorized;
}

export async function disconnectAppleMusic(): Promise<void> {
  rememberConnected(false);
  try {
    const music = await getMusicKit();
    await music.unauthorize();
  } catch {
    // Nothing to undo if MusicKit never loaded.
  }
}

interface RawTrack {
  id?: string;
  type?: string;
  attributes?: {
    name?: string;
    artistName?: string;
    albumName?: string;
    artwork?: { url?: string };
    previews?: Array<{ url?: string }>;
    playParams?: { catalogId?: string; id?: string };
  };
}

/**
 * Recently played songs, as display rows keyed by CATALOG id (library songs
 * carry theirs in `playParams.catalogId`; a library-only upload has none and
 * is skipped — there is nothing in the catalog to save).
 */
export async function getAppleMusicRecentlyPlayed(): Promise<MusicSong[]> {
  const music = await getMusicKit();
  if (!music.isAuthorized) return [];
  const response = await music.api.music("/v1/me/recent/played/tracks", {
    types: "songs,library-songs",
    limit: 30,
  });
  const seen = new Set<string>();
  const songs: MusicSong[] = [];
  for (const raw of (response.data?.data ?? []) as RawTrack[]) {
    const a = raw.attributes;
    const catalogId = raw.type === "songs" ? raw.id : a?.playParams?.catalogId;
    if (!catalogId || !/^\d{1,20}$/.test(catalogId) || seen.has(catalogId)) {
      continue;
    }
    if (!a?.name || !a.artistName) continue;
    seen.add(catalogId);
    songs.push({
      provider: "apple_music",
      id: catalogId,
      title: a.name,
      artistName: a.artistName,
      albumName: a.albumName ?? null,
      artworkUrl: a.artwork?.url ?? null,
      artworkBgColor: null,
      artworkTextColor: null,
      previewUrl: a.previews?.[0]?.url ?? null,
      providerUrl: null,
      durationMs: null,
      explicit: false,
      isrc: null,
      genreNames: [],
      artists: [],
    });
  }
  return songs;
}
